class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = document.getElementById("cart-drawer-panel");
    this.overlay = document.getElementById("cart-drawer-overlay");
    this.onBodyClick = this.handleCloseClick.bind(this);
    this.onBodyOpenClick = this.handleOpenClick.bind(this);
    this.renderCart = this.handleRenderCart.bind(this);
    this.removeItem = this.handleRemoveItem.bind(this);
    this.clearCart = this.handleClearCart.bind(this);
    this.isFirstLoad = true;

    // Get customer's warehouse code from meta tag
    this.customerWarehouse = document.querySelector(
      'meta[name="customer-warehouse"]',
    )?.content;

    // if customerWarehouse is MON, replace it with RBM
    if (this.customerWarehouse === "MON") {
      this.customerWarehouse = "RBM";
    }

    // Define warehouse locations (same as location-inventory.js)
    this.locations = [
      {
        name: "Sydney, NSW",
        code: "GIR",
      },
      {
        name: "Brisbane, QLD",
        code: "WOO",
      },
      {
        name: "Melbourne, VIC",
        code: "RBM",
      },
    ];

    // Find customer's warehouse location
    this.customerWarehouseLocation = this.locations.find(
      (loc) => loc.code === this.customerWarehouse,
    );

    this.cartItemsContainer = document.getElementById("js-cart-items");

    this.openTriggers = document.querySelectorAll(".js-btn-open-cart");
    this.closeTiggers = document.querySelectorAll(".js-btn-close-cart");

    this.openTriggers.forEach((btn) => {
      btn.addEventListener("click", this.onBodyOpenClick);
    });

    this.closeTiggers.forEach((btn) => {
      btn.addEventListener("click", this.onBodyClick);
    });
  }

  async connectedCallback() {
    await this.loadCart();

    // Add clear cart button event listener
    const clearCartButton = this.querySelector(".js-clear-cart");
    if (clearCartButton) {
      clearCartButton.addEventListener("click", this.clearCart);
    }
  }

  showLoading() {
    const loadingEl = document.getElementById("js-cart-loading");
    const cartItemsEl = document.getElementById("js-cart-items");
    if (loadingEl && cartItemsEl) {
      loadingEl.classList.remove("hidden");
      cartItemsEl.style.opacity = "0.15";
      cartItemsEl.style.pointerEvents = "none";
    }
  }

  hideLoading() {
    const loadingEl = document.getElementById("js-cart-loading");
    const cartItemsEl = document.getElementById("js-cart-items");
    if (loadingEl && cartItemsEl) {
      loadingEl.classList.add("hidden");
      cartItemsEl.style.opacity = "1";
      cartItemsEl.style.pointerEvents = "auto";
    }
  }

  async loadCart() {
    this.showLoading();

    // First try to get the cart with detailed information
    try {
      // Try to get more detailed cart information first
      const detailedResponse = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}), // Empty update to refresh cart data
      });

      if (detailedResponse.ok) {
        const cart = await detailedResponse.json();
        this.renderCart(cart);
        return;
      }
    } catch (error) {
      console.error("Error fetching detailed cart information:", error);
    }

    // Fallback to standard cart.js if the detailed approach fails
    const response = await fetch("/cart.js");
    const cart = await response.json();
    this.renderCart(cart);
  }

  async checkWarehouseAvailability(sku) {
    try {
      const apiUrl = `https://api.partbot.io/v1/products/${sku}`;
      const apiKey = "pb_868d33e4423a20a949c9a229e45adf81";

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Basic ${apiKey}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      if (result.stock) {
        const customerWarehouseStock = result.stock.find(
          (item) => item.warehouse_code === this.customerWarehouse,
        );
        return customerWarehouseStock?.availability === "In Stock";
      }
      return false;
    } catch (error) {
      console.error("Error checking warehouse availability:", error);
      return false;
    }
  }

  async handleRenderCart(cart) {
    console.log("Initial cart items:", cart.items);
    // Group items by warehouse based on availability
    const warehouseGroups = {};

    // Initialize groups with proper warehouse names
    if (this.customerWarehouseLocation) {
      warehouseGroups[this.customerWarehouseLocation.name] = [];
    }
    warehouseGroups["Alternate Warehouse"] = [];
    warehouseGroups["Backordered"] = [];

    // Get customer's warehouse code from meta tag (in case it changed)
    this.customerWarehouse = document.querySelector(
      'meta[name="customer-warehouse"]',
    )?.content;

    // if customerWarehouse is MON, replace it with RBM
    if (this.customerWarehouse === "MON") {
      this.customerWarehouse = "RBM";
    }

    // Find customer's warehouse location (in case it changed)
    console.log("Customer warehouse:", this.customerWarehouse);
    console.log("Locations:", this.locations);
    this.customerWarehouseLocation = this.locations.find(
      (loc) => loc.code === this.customerWarehouse,
    );

    console.log("Current customer warehouse:", this.customerWarehouseLocation);

    // Helper function to add or update item in a group
    const addToGroup = (group, item, quantity, pricePerItem) => {
      const existingItem = group.find((i) => i.variant_id === item.variant_id);
      if (existingItem) {
        existingItem.quantity += quantity;
        existingItem.final_line_price += pricePerItem * quantity;
      } else {
        group.push({
          ...item,
          quantity: quantity,
          final_line_price: pricePerItem * quantity,
        });
      }
    };

    try {
      // Process each item and check its warehouse availability
      for (const item of cart.items) {
        // Fetch stock data for the item
        const stockData = await this.fetchStockData(item.sku);

        // Calculate total available stock in customer's warehouse
        const customerWarehouseStock = stockData.find(
          (stock) => stock.warehouse_code === this.customerWarehouse,
        );
        const customerWarehouseQty =
          customerWarehouseStock?.availability === "In Stock"
            ? customerWarehouseStock.qty_on_hand
            : 0;

        // Calculate total available stock across all warehouses
        const totalAvailableStock = stockData.reduce((total, stock) => {
          return stock.availability === "In Stock"
            ? total + stock.qty_on_hand
            : total;
        }, 0);

        const pricePerItem = item.final_line_price / item.quantity;

        // If requested quantity exceeds customer warehouse stock but is available elsewhere
        if (
          item.quantity > customerWarehouseQty &&
          totalAvailableStock >= item.quantity
        ) {
          // Split between customer warehouse and alternate warehouse
          const customerWarehouseAllocation = Math.min(
            customerWarehouseQty,
            item.quantity,
          );
          const alternateWarehouseQty =
            item.quantity - customerWarehouseAllocation;

          if (
            customerWarehouseAllocation > 0 &&
            this.customerWarehouseLocation
          ) {
            addToGroup(
              warehouseGroups[this.customerWarehouseLocation.name],
              item,
              customerWarehouseAllocation,
              pricePerItem,
            );
          }

          if (alternateWarehouseQty > 0) {
            addToGroup(
              warehouseGroups["Alternate Warehouse"],
              item,
              alternateWarehouseQty,
              pricePerItem,
            );
          }
        } else {
          // Handle backorder case
          const backorderedQuantity = Math.max(
            0,
            item.quantity - totalAvailableStock,
          );
          const availableQuantity = item.quantity - backorderedQuantity;

          if (backorderedQuantity > 0) {
            addToGroup(
              warehouseGroups["Backordered"],
              item,
              backorderedQuantity,
              pricePerItem,
            );
          }

          if (availableQuantity > 0) {
            if (customerWarehouseQty > 0 && this.customerWarehouseLocation) {
              addToGroup(
                warehouseGroups[this.customerWarehouseLocation.name],
                item,
                availableQuantity,
                pricePerItem,
              );
            } else {
              addToGroup(
                warehouseGroups["Alternate Warehouse"],
                item,
                availableQuantity,
                pricePerItem,
              );
            }
          }
        }
      }

      console.log("Final warehouse groups:", warehouseGroups);

      // Get current cart notes
      const currentCartResponse = await fetch("/cart.js");
      const currentCart = await currentCartResponse.json();
      const currentNotes = currentCart.note ? currentCart.note.split("; ") : [];
      console.log("Current cart notes:", currentNotes);

      // Prepare new notes based on current cart state
      let newNotes = [];

      // Add backordered note if needed
      if (warehouseGroups["Backordered"].length > 0) {
        newNotes.push("Backordered items included.");
      }

      // Add warehouse notes if needed
      if (warehouseGroups["Alternate Warehouse"].length > 0) {
        if (warehouseGroups[this.customerWarehouseLocation?.name]?.length > 0) {
          newNotes.push(
            "Mixed warehouse order: Items shipping from different locations",
          );
        } else {
          newNotes.push(
            "Alternate warehouse order: Items shipping from different location",
          );
        }
      }

      // Add any other existing notes that aren't related to warehouses or backorders
      newNotes.push(
        ...currentNotes.filter(
          (note) =>
            !note.includes("warehouse") &&
            !note.includes("Backordered items included"),
        ),
      );

      // Update cart with new notes
      const noteUpdateResponse = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          note: newNotes.join("; "),
        }),
      });

      console.log("Updated cart notes:", newNotes);

      // Show modals in the correct order
      const hasAlternateWarehouse =
        warehouseGroups["Alternate Warehouse"].length > 0;
      const hasBackordered = warehouseGroups["Backordered"].length > 0;

      // Always show alternate warehouse modal first if there are items from alternate warehouses
      if (hasAlternateWarehouse) {
        const alternateWarehouseModal = document.getElementById(
          "js-alternate-warehouse-modal",
        );
        if (alternateWarehouseModal) {
          alternateWarehouseModal.classList.remove("hidden");
          // Wait for user to accept alternate warehouse
          await new Promise((resolve) => {
            const acceptButton = alternateWarehouseModal.querySelector(
              '[data-action="proceed-alternate-warehouse"]',
            );
            const cancelButton = alternateWarehouseModal.querySelector(
              '[data-action="cancel-alternate-warehouse"]',
            );

            const handleAccept = () => {
              alternateWarehouseModal.classList.add("hidden");
              acceptButton.removeEventListener("click", handleAccept);
              cancelButton.removeEventListener("click", handleCancel);
              resolve();
            };

            const handleCancel = () => {
              alternateWarehouseModal.classList.add("hidden");
              acceptButton.removeEventListener("click", handleAccept);
              cancelButton.removeEventListener("click", handleCancel);
              resolve();
            };

            acceptButton.addEventListener("click", handleAccept);
            cancelButton.addEventListener("click", handleCancel);
          });
        }
      }

      // Then show backorder modal if needed
      if (hasBackordered) {
        const backorderModal = document.getElementById("js-backorder-modal");
        if (backorderModal) {
          backorderModal.classList.remove("hidden");
          // Wait for user to accept backorder
          await new Promise((resolve) => {
            const acceptButton = backorderModal.querySelector(
              '[data-action="proceed-backorder"]',
            );
            const cancelButton = backorderModal.querySelector(
              '[data-action="cancel-backorder"]',
            );

            const handleAccept = () => {
              backorderModal.classList.add("hidden");
              acceptButton.removeEventListener("click", handleAccept);
              cancelButton.removeEventListener("click", handleCancel);
              resolve();
            };

            const handleCancel = () => {
              backorderModal.classList.add("hidden");
              acceptButton.removeEventListener("click", handleAccept);
              cancelButton.removeEventListener("click", handleCancel);
              resolve();
            };

            acceptButton.addEventListener("click", handleAccept);
            cancelButton.addEventListener("click", handleCancel);
          });
        }
      }

      // Generate HTML for each warehouse group
      const cartHtml = Object.entries(warehouseGroups)
        .filter(([_, items]) => items.length > 0) // Only show groups with items
        .map(([warehouse, items]) => {
          const itemsHtml = items
            .map(
              (item) => `
          <li class="py-3 flex border-b border-dashed border-gray-300" 
              data-group-type="${warehouse}"
              data-variant-id="${item.variant_id}"
              data-quantity="${item.quantity}"
              data-sku="${item.sku}">
            <div class="flex-shrink-0 w-12 h-12 border border-gray-200 rounded-md overflow-hidden">
              <img src="${item.image}" alt="${item.product_title}" class="w-full h-full object-center object-cover">
            </div>
            <div class="ml-4 flex-1 flex flex-col">
              <div>
                <div class="flex justify-between text-xs font-semibold tracking-tight leading-0">
                  <h3 class="text-gray-900 block">
                    <a href="${item.url}">
                      ${item.product_title}
                    </a>
                  </h3>
                  <p class="ml-4 font-bold text-gray-500">
                    $${(item.final_line_price / 100.0).toFixed(2)}
                  </p>
                </div>
                ${
                  item.variant_title
                    ? `<p class="mt-1 text-xs text-gray-500">
                      ${item.variant_title}
                    </p>`
                    : ""
                }
              </div>
              <div class="flex-1 flex items-end font-sans justify-between text-xs mt-4">
                <div class="flex items-center gap-3">
                  <p class="text-gray-500">
                    <span class="font-medium text-gray-800">${item.sku}</span> / <span class="font-medium text-gray-800">Qty ${item.quantity}</span> / $${(
                      item.final_line_price /
                      100.0 /
                      item.quantity
                    ).toFixed(2)}
                  </p>
                </div>
                <div class="flex">
                  <button type="button" 
                          data-variant-id="${item.variant_id}" 
                          data-group-type="${warehouse}"
                          data-quantity="${item.quantity}"
                          class="js-remove-cart-item font-medium text-red-600 hover:text-red-500">Remove</button>
                </div>
              </div>
            </div>
          </li>
        `,
            )
            .join("");

          return `
          <div class="mb-6">
            <div class="flex flex-col gap-2 items-start justify-start">
              <div class="text-xs font-semibold text-gray-900 px-2 py-1 rounded-md bg-gray-200">${warehouse}</div>
              <p class="text-xs text-amber-600">
                ${
                  warehouse === "Alternate Warehouse"
                    ? "Additional shipping charges may apply"
                    : warehouse === "Backordered"
                      ? "Items will be shipped when back in stock"
                      : ""
                }
              </p>
            </div>
            <ul role="list" class="divide-y divide-gray-200">
              ${itemsHtml}
            </ul>
          </div>
        `;
        })
        .join("");

      this.cartItemsContainer.innerHTML =
        cartHtml ||
        `
        <div class="text-center text-xs text-gray-500 py-8">
          Your cart is empty
        </div>
      `;

      // Show/hide clear cart button based on cart items
      const clearCartButton = this.querySelector(".js-clear-cart");
      if (clearCartButton) {
        clearCartButton.classList.toggle("hidden", cart.item_count === 0);
      }

      // Display discounts if any
      this.displayDiscounts(cart);

      // Set totals
      document.querySelectorAll(".js-cart-total").forEach((field) => {
        field.innerHTML = `$${(cart.total_price / 100.0).toFixed(2)}`;
      });

      // Set cart totals
      document.querySelectorAll(".js-cart-qty").forEach((field) => {
        field.innerHTML = cart.item_count;
      });

      // Add event listeners
      document.querySelectorAll(".js-remove-cart-item").forEach((btn) => {
        btn.addEventListener("click", this.removeItem);
      });
    } catch (error) {
      console.error("Error rendering cart:", error);
      this.cartItemsContainer.innerHTML = `
        <div class="text-center text-red-500 py-8">
          Error loading cart. Please try again.
        </div>
      `;
    } finally {
      this.hideLoading();
    }
  }

  async fetchStockData(sku) {
    const apiUrl = `https://api.partbot.io/v1/products/${sku}`;
    const apiKey = "pb_868d33e4423a20a949c9a229e45adf81";

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Basic ${apiKey}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      return result.stock || [];
    } catch (error) {
      console.error("Error fetching stock data:", error);
      return [];
    }
  }

  // Display discounts in the cart drawer
  displayDiscounts(cart) {
    const discountsContainer = document.getElementById("js-cart-discounts");
  }

  open() {
    // this.removeAttribute("hidden");
    this.classList.remove("pointer-events-none");
    this.overlay.classList.remove("opacity-0");
    this.overlay.classList.add("opacity-100");
    this.drawer.classList.remove("translate-x-full");
    this.drawer.classList.add("translate-x-0");

    // this.notification.addEventListener('transitionend', () => {
    //   this.notification.focus();
    //   trapFocus(this.notification);
    // }, { once: true });

    this.overlay.addEventListener("click", this.onBodyClick);
  }

  close() {
    // this.setAttribute("hidden", true);
    this.classList.add("pointer-events-none");
    this.overlay.classList.remove("opacity-100");
    this.overlay.classList.add("opacity-0");
    this.drawer.classList.remove("translate-x-0");
    this.drawer.classList.add("translate-x-full");

    this.overlay.removeEventListener("click", this.onBodyClick);

    // removeTrapFocus(this.activeElement);
  }

  async handleRemoveItem(evt) {
    this.showLoading();

    const button = evt.target;
    const variantId = parseInt(button.dataset.variantId);
    const groupType = button.dataset.groupType;
    const groupQuantity = parseInt(button.dataset.quantity);

    // Get current cart data
    const currentCartResponse = await fetch("/cart.js");
    const currentCart = await currentCartResponse.json();
    const cartItem = currentCart.items.find(
      (item) => item.variant_id === variantId,
    );

    if (!cartItem) return;

    // Calculate new quantity by removing the group quantity
    const newQuantity = Math.max(0, cartItem.quantity - groupQuantity);

    // Update cart
    const response = await fetch("/cart/update.js", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        updates: {
          [variantId]: newQuantity,
        },
      }),
    });

    const cart = await response.json();
    this.renderCart(cart);
  }

  handleCloseClick(evt) {
    this.close();
  }

  handleOpenClick(evt) {
    this.open();
  }

  async handleClearCart() {
    if (!confirm("Are you sure you want to clear your cart?")) return;

    this.showLoading();

    try {
      const response = await fetch("/cart/clear.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const cart = await response.json();
        this.renderCart(cart);
      }
    } catch (error) {
      console.error("Error clearing cart:", error);
      this.hideLoading();
    }
  }
}

customElements.define("cart-drawer", CartDrawer);
